import React, {useState, useEffect} from 'react';

import YouTubeVideo from '../../components/YouTubeVideo';
import Button, {ButtonSizes} from '../../components/ButtonNew';
import StarRating from '../../components/StarRating';
import {Checkmark, HollowPlayIcon} from '../../components/svg';
import {getQuestionRating, postQuestionRating, logVideoView} from '../../api/api';
import {useAppContext, useAnalytics} from '../../hooks';
import {VIDEO_PREVIEW_URL} from '../../constants/content';

const upgradeFeatures = [
  '100+ expert answer videos',
  'Access to all courses',
  'Peer-to-peer practice',
  'Private Slack community',
  'Full question database',
];

const UPGRADE_URL = '/upgrade?src=questions'; // TODO: Customize to role

const VideoAnswer = ({question}) => {
  const {currentUser, hasSubscription} = useAppContext();
  const {tracker} = useAnalytics();
  const [userRating, setUserRating] = useState(null);
  const [hasRated, setHasRated] = useState(false);
  const [ratingExists, setRatingExists] = useState(false);

  useEffect(() => {
    if (hasSubscription) {
      tracker.questionViewVideo();
    } else {
      tracker.questionViewVideoUpsell();
    }

    if (currentUser) {
      getQuestionRating(question.id).then((rating) => {
        if (rating) {
          setUserRating(rating);
          setRatingExists(true);
        } else {
          setUserRating(0);
        }
      });
    }
  }, []);

  const onPlayVideo = () => {
    tracker.questionPlayVideo({question: question.id});
    logVideoView(question.id);
  };

  const handleRating = async (rating) => {
    await postQuestionRating(question.id, rating);
    setHasRated(true);
  };

  const renderRatingText = () => {
    if (hasRated) return 'Thanks for the feedback!';
    if (!ratingExists) return 'Was this video helpful?';
    return null;
  };

  if (!question || !question.data || !question.data.video) {
    return null;
  }

  const {video, trailer} = question.data;
  const {id, author} = video;
  const showUpsell = (!currentUser || !hasSubscription);
  const trailerURL = `https://app.chapterme.co/trailerSelection/${trailer}`;

  // Non-subscriber upsell with blurred video or displays trailer if available
  if (showUpsell) {
    return (
      <div
        className="flex flex-col justify-between mt-6 p-3 bg-gray-50 border border-gray-200 md:rounded-lg md:flex-row"
      >
        {trailer ? (
          <iframe
            title="preview"
            className="bg-black h-72 flex w-full relative rounded overflow-hidden cursor-pointer"
            src={trailerURL}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            frameBorder="0"
            scrolling="no"
            loading="lazy"
            allowFullScreen
          />
        ) : (
          <button
            onClick={() => tracker.questionClickVideoUpsell()}
            className="flex flex-col w-full relative rounded overflow-hidden cursor-pointer"
            type="button"
          >
            <video
              className="VideoAnswer__blur"
              src={VIDEO_PREVIEW_URL}
              autoPlay
              muted
              loop
              playsInline
            />
            <div className="flex flex-col items-center justify-center py-4 px-8 absolute top-0 w-full h-full bg-black bg-opacity-75">
              <div className="flex flex-col items-center text-center mt-10 mb-10">
                <span className="text-sm sm:text-lg text-white font-semibold mb-1 flex items-center">
                  <HollowPlayIcon width={24} height={24} className="mr-2" />
                  Upgrade to see our video answer
                </span>
                <span className="VideoAnswer__subtitle w-3/4 text-base text-white leading-tight font-medium text-center opacity-75">
                  {`See how a real ${author && author.role ? author.role : 'interviewer'} answers this question in our expert video`}
                </span>
              </div>
              {author && (
              <div className="flex items-center">
                <img
                  className="rounded-full h-10 w-10 mr-4"
                  src={author.image}
                  alt={author.name}
                />
                <div className="flex flex-col">
                  <span className="text-sm text-white text-left font-semibold leading-tight">
                    {author.name}
                    <br />
                    {author.title}
                  </span>
                </div>
              </div>
              )}
            </div>
          </button>
        )}
        <div className="flex flex-col items-center whitespace-nowrap py-3 md:p-8">
          <span className="text-lg font-semibold">
            Become a member today
          </span>
          <ul>
            {upgradeFeatures.map((f) => (
              <li className="font-medium text-gray-900 my-1">
                <Checkmark />
                <span className="ml-2">{f}</span>
              </li>
            ))}
          </ul>
          <Button
            href={UPGRADE_URL}
            onClick={() => tracker.questionClickVideoUpgrade()}
            size={ButtonSizes.LARGE}
            className="w-full mt-3"
          >
            Upgrade now
          </Button>
        </div>
      </div>
    );
  }

  // Subscriber youtube video
  return (
    <div className="flex flex-col md:flex-row justify-center w-full items-center mt-6 px-3 md:px-0">
      <div className="flex flex-col items-end justify-center w-full">
        <YouTubeVideo
          videoID={id}
          onPlay={onPlayVideo}
        />
        <div className="VideoAnswer__stars flex">
          <p className="text-sm sm:text-lg font-bold mt-2 mr-4 sm:mt-1.5 sm:mb-1">{renderRatingText()}</p>
          <div className="text-xl sm:text-3xl">
            {typeof userRating === 'number' && (
              <StarRating onRating={handleRating} initialRating={userRating} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoAnswer;
